import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import TestStudentsSection from "./TestStudentsSection";
import QueueMessagesSection from "./QueueMessagesSection";
import ProfileCard from "../shared/ProfileCard";
import ExportCard from "./ExportCard";
import styles from "./ManageTab.module.css";
import PolicyImageSection from "./PolicyImageSection";

export default function ManageTab() {
  const { apiFetch } = useAuth();

  const [courses, setCourses] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [resendingAll, setResendingAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [courseCode, setCourseCode] = useState("");
  const [courseName, setCourseName] = useState("");
  const [maxSessions, setMaxSessions] = useState("1");
  const [semesters, setSemesters] = useState([]);
  const [newSemesterId, setNewSemesterId] = useState("");
  const [editCourse, setEditCourse] = useState(null); // { id, code, semester_id }
  const [enrollCourse, setEnrollCourse] = useState("");
  const [enrollEmail, setEnrollEmail] = useState("");
  const [singleFirst, setSingleFirst] = useState("");
  const [singleLast, setSingleLast] = useState("");
  const [sendEmails, setSendEmails] = useState(true);
  const [importCourse, setImportCourse] = useState("");
  const [compResult, setCompResult] = useState(null);
  const [smtp, setSmtp] = useState({
    host: "",
    port: 587,
    username: "",
    from_email: "",
    from_name: "",
    use_tls: true,
  });
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpEncAvailable, setSmtpEncAvailable] = useState(true);
  const [smtpResult, setSmtpResult] = useState(null);
  const [smtpBusy, setSmtpBusy] = useState(false);
  const [cal, setCal] = useState({
    enabled: false,
    marker: "Student Hours",
    early_minutes: 15,
    default_policy: "FCFS",
    pool_shuffle_offset_minutes: 1,
  });
  const [calUrl, setCalUrl] = useState("");
  const [calStatus, setCalStatus] = useState({
    is_configured: false,
    last_sync_at: null,
    last_sync_error: null,
    consecutive_failures: 0,
  });
  const [calPreview, setCalPreview] = useState(null);
  const [calResult, setCalResult] = useState(null);
  const [calBusy, setCalBusy] = useState(false);
  const [importStudentResult, setImportStudentResult] = useState(null);
  const [rosterCourse, setRosterCourse] = useState("");
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [confirmStudent, setConfirmStudent] = useState(null); // { student, deactivate }
  const [archived, setArchived] = useState([]);
  const [compCourse, setCompCourse] = useState("");
  const [competencies, setCompetencies] = useState([]);
  const [compLoading, setCompLoading] = useState(false);

  useEffect(() => {
    loadCourses();
    loadArchived();
    loadSmtp();
    loadCalendar();
    loadSemesters();
  }, []); // eslint-disable-line

  const loadSemesters = async () => {
    try {
      const res = await apiFetch("/api/courses/semesters/list");
      if (res.ok) {
        const list = await res.json();
        setSemesters(list);
        // Default the create picker to the first available semester.
        if (list.length && !newSemesterId) setNewSemesterId(String(list[0].id));
      }
    } catch {
      /* leave empty */
    }
  };

  const loadCalendar = async () => {
    try {
      const res = await apiFetch("/api/settings/calendar");
      if (res.ok) {
        const d = await res.json();
        setCal({
          enabled: !!d.enabled,
          marker: d.marker || "Student Hours",
          early_minutes: d.early_minutes ?? 15,
          default_policy: d.default_policy || "FCFS",
          pool_shuffle_offset_minutes: d.pool_shuffle_offset_minutes ?? 1,
        });
        setCalStatus({
          is_configured: !!d.is_configured,
          last_sync_at: d.last_sync_at,
          last_sync_error: d.last_sync_error,
          consecutive_failures: d.consecutive_failures || 0,
        });
      }
    } catch {
      /* ignore */
    }
  };

  const saveCalendar = async (overrides = {}) => {
    setCalBusy(true);
    setCalResult(null);
    try {
      const body = { ...cal, ...overrides };
      if (calUrl.trim()) body.ics_url = calUrl.trim();
      const res = await apiFetch("/api/settings/calendar", {
        method: "PUT",
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setCalUrl("");
        setCalResult({ ok: !d.warning, text: d.message });
        await loadCalendar();
      } else {
        setCalResult({
          ok: false,
          text: d.detail || "Could not save calendar settings.",
        });
      }
    } catch (err) {
      setCalResult({ ok: false, text: err.message });
    } finally {
      setCalBusy(false);
    }
  };

  const previewCalendar = async () => {
    setCalBusy(true);
    setCalResult(null);
    setCalPreview(null);
    try {
      const res = await apiFetch("/api/settings/calendar/preview", {
        method: "POST",
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) setCalPreview(d);
      else setCalResult({ ok: false, text: d.detail || "Preview failed." });
    } catch (err) {
      setCalResult({ ok: false, text: err.message });
    } finally {
      setCalBusy(false);
    }
  };

  const syncCalendarNow = async () => {
    setCalBusy(true);
    setCalResult(null);
    try {
      const res = await apiFetch("/api/settings/calendar/sync", {
        method: "POST",
      });
      const d = await res.json().catch(() => ({}));
      setCalResult(
        res.ok
          ? { ok: true, text: d.message }
          : { ok: false, text: d.detail || "Sync failed." },
      );
      await loadCalendar();
    } catch (err) {
      setCalResult({ ok: false, text: err.message });
    } finally {
      setCalBusy(false);
    }
  };

  const disableCalendar = async () => {
    if (
      !window.confirm("Turn off auto-open and remove the saved calendar link?")
    )
      return;
    setCalBusy(true);
    setCalResult(null);
    try {
      const res = await apiFetch("/api/settings/calendar", {
        method: "DELETE",
      });
      if (res.ok) {
        setCalPreview(null);
        setCalResult({ ok: true, text: "Calendar auto-open disabled." });
        await loadCalendar();
      }
    } catch (err) {
      setCalResult({ ok: false, text: err.message });
    } finally {
      setCalBusy(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const loadSmtp = async () => {
    try {
      const res = await apiFetch("/api/settings/smtp");
      if (res.ok) {
        const d = await res.json();
        setSmtp({
          host: d.host || "",
          port: d.port || 587,
          username: d.username || "",
          from_email: d.from_email || "",
          from_name: d.from_name || "",
          use_tls: d.use_tls !== false,
        });
        setSmtpConfigured(!!d.is_configured);
        setSmtpEncAvailable(d.encryption_available !== false);
      }
    } catch {
      /* ignore */
    }
  };

  const saveSmtp = async () => {
    if (!smtp.host || !smtp.username) {
      setSmtpResult({ ok: false, text: "Host and username are required." });
      return;
    }
    setSmtpBusy(true);
    setSmtpResult(null);
    try {
      const body = { ...smtp };
      if (smtpPassword) body.password = smtpPassword;
      const res = await apiFetch("/api/settings/smtp", {
        method: "PUT",
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setSmtpResult({
          ok: true,
          text: "Saved. Send a test to confirm it works.",
        });
        setSmtpPassword("");
        setSmtpConfigured(true);
      } else {
        setSmtpResult({
          ok: false,
          text: d.detail || "Could not save SMTP settings.",
        });
      }
    } catch (err) {
      setSmtpResult({ ok: false, text: err.message });
    } finally {
      setSmtpBusy(false);
    }
  };

  const testSmtp = async () => {
    setSmtpBusy(true);
    setSmtpResult(null);
    try {
      const res = await apiFetch("/api/settings/smtp/test", { method: "POST" });
      const d = await res.json().catch(() => ({}));
      setSmtpResult(
        res.ok
          ? {
              ok: true,
              text: `${d.message}${d.personal ? "" : " (sent via the system account)"}`,
            }
          : { ok: false, text: d.detail || "Test email failed." },
      );
    } catch (err) {
      setSmtpResult({ ok: false, text: err.message });
    } finally {
      setSmtpBusy(false);
    }
  };

  const clearSmtp = async () => {
    if (
      !window.confirm(
        "Remove your personal email settings and revert to the system sender?",
      )
    )
      return;
    setSmtpBusy(true);
    setSmtpResult(null);
    try {
      const res = await apiFetch("/api/settings/smtp", { method: "DELETE" });
      if (res.ok) {
        setSmtp({
          host: "",
          port: 587,
          username: "",
          from_email: "",
          from_name: "",
          use_tls: true,
        });
        setSmtpPassword("");
        setSmtpConfigured(false);
        setSmtpResult({
          ok: true,
          text: "Reverted to the system email sender.",
        });
      }
    } catch (err) {
      setSmtpResult({ ok: false, text: err.message });
    } finally {
      setSmtpBusy(false);
    }
  };

  const loadCourses = async () => {
    try {
      // Use /api/courses/mine so admins only see their own courses here
      const res = await apiFetch("/api/courses/mine");
      if (res.ok) setCourses(await res.json());
    } catch {
      /* ignore */
    }
  };

  const loadArchived = async () => {
    try {
      const res = await apiFetch("/api/courses/mine/archived");
      if (res.ok) setArchived(await res.json());
    } catch {
      /* ignore */
    }
  };

  const loadCompetencies = async (courseId) => {
    if (!courseId) {
      setCompetencies([]);
      return;
    }
    setCompLoading(true);
    try {
      const res = await apiFetch(`/api/courses/${courseId}/competencies`);
      setCompetencies(res.ok ? await res.json() : []);
    } catch {
      setCompetencies([]);
    } finally {
      setCompLoading(false);
    }
  };

  const handleCourseCompImport = async (e) => {
    const file = e.target.files[0];
    e.target.value = ""; // reset immediately so the same file can be re-picked
    if (!file) return;
    if (!compCourse) {
      flash("Select a course first.", true);
      return;
    }
    setCompLoading(true);
    setCompResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(
        `/api/courses/${compCourse}/import-competencies`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("gradiance_token")}`,
          },
          body: formData,
        },
      );
      let data = null;
      try {
        data = await res.json();
      } catch {
        /* non-JSON error body */
      }

      if (res.ok && data?.success) {
        const s = data.summary || {};
        setCompResult({
          ok: true,
          text:
            `${data.course_code}: ${s.competencies_added} added, ` +
            `${s.competencies_kept} kept, ${s.competencies_removed} removed.`,
          warnings: data.warnings || [],
        });
        await loadCompetencies(compCourse);
        await loadCourses(); // course code may have been renamed to match the file
      } else {
        setCompResult({
          ok: false,
          text: data?.detail || `Import failed (HTTP ${res.status}).`,
          warnings: [],
        });
      }
    } catch (err) {
      setCompResult({ ok: false, text: err.message, warnings: [] });
    } finally {
      setCompLoading(false);
    }
  };

  const handleArchive = async (course) => {
    if (
      !window.confirm(
        `Archive ${course.course_code}? It will be hidden from active lists and ` +
          `the queue, but nothing is deleted — you can restore it below.`,
      )
    )
      return;
    try {
      const res = await apiFetch(`/api/courses/${course.id}/archive`, {
        method: "POST",
      });
      if (res.ok) {
        flash(`${course.course_code} archived.`);
        // Selectors pointing at the archived course should reset
        if (String(compCourse) === String(course.id)) {
          setCompCourse("");
          setCompetencies([]);
        }
        if (String(rosterCourse) === String(course.id)) {
          setRosterCourse("");
          setRoster([]);
        }
        if (String(importCourse) === String(course.id)) setImportCourse("");
        if (String(enrollCourse) === String(course.id)) setEnrollCourse("");
        await loadCourses();
        await loadArchived();
      } else {
        const data = await res.json().catch(() => ({}));
        flash(data.detail || "Failed to archive course.", true);
      }
    } catch (err) {
      flash(err.message, true);
    }
  };

  const handleRestore = async (course) => {
    try {
      const res = await apiFetch(`/api/courses/${course.id}/restore`, {
        method: "POST",
      });
      if (res.ok) {
        flash(`${course.course_code} restored.`);
        await loadCourses();
        await loadArchived();
      } else {
        const data = await res.json().catch(() => ({}));
        flash(data.detail || "Failed to restore course.", true);
      }
    } catch (err) {
      flash(err.message, true);
    }
  };

  const handleDeletePermanently = async (course) => {
    if (
      !window.confirm(
        `Permanently delete ${course.course_code}?\n\n` +
          `This scrubs the course and ALL related data — enrollments, queue ` +
          `history, and competency links. This CANNOT be undone.`,
      )
    )
      return;
    // Second gate: require typing the course code to confirm.
    const typed = window.prompt(
      `To confirm permanent deletion, type the course code exactly: ${course.course_code}`,
    );
    if (typed == null) return;
    if (typed.trim().toUpperCase() !== course.course_code.toUpperCase()) {
      flash("Course code did not match — nothing was deleted.", true);
      return;
    }
    try {
      // The server validates `confirm` too — the prompt above only protects
      // people coming through this UI, not anyone calling the API directly.
      const res = await apiFetch(
        `/api/courses/${course.id}?confirm=${encodeURIComponent(typed.trim())}`,
        { method: "DELETE" },
      );
      if (res.ok) {
        flash(`${course.course_code} permanently deleted.`);
        await loadCourses();
        await loadArchived();
      } else {
        const data = await res.json().catch(() => ({}));
        // A 409 returns { message, blockers } rather than a string, so say what
        // is actually in the way instead of rendering [object Object].
        const detail = data.detail;
        if (detail && typeof detail === "object") {
          const blockers = (detail.blockers || [])
            .map((b) => b.course_code || b.type)
            .join(", ");
          flash(
            blockers ? `${detail.message} (${blockers})` : detail.message,
            true,
          );
        } else {
          flash(
            typeof detail === "string" ? detail : "Failed to delete course.",
            true,
          );
        }
      }
    } catch (err) {
      flash(err.message, true);
    }
  };

  const resendSetup = async (student) => {
    setResendingId(student.id);
    try {
      const res = await apiFetch(
        `/api/courses/${rosterCourse}/students/${student.id}/resend-setup`,
        { method: "POST" },
      );
      const d = await res.json().catch(() => ({}));
      if (res.ok)
        flash(d.message || `New setup link sent to ${student.email}.`);
      else
        flash(
          typeof d.detail === "string" ? d.detail : "Could not send the link.",
          true,
        );
    } catch (err) {
      flash(err.message, true);
    } finally {
      setResendingId(null);
    }
  };

  // Notify every student on this roster who hasn't set up yet. Confirmed first,
  // because each press mints new tokens: a second press sends everyone another
  // email and invalidates the first.
  const resendSetupAll = async () => {
    const pending = roster.filter(
      (s) => s.is_active !== false && !s.password_set,
    ).length;
    if (pending === 0) return;
    if (
      !window.confirm(
        `Send a new setup link to ${pending} student${pending === 1 ? "" : "s"} ` +
          `who haven't set up their account?\n\n` +
          `Any earlier setup email they have will stop working.`,
      )
    )
      return;

    setResendingAll(true);
    try {
      const res = await apiFetch(
        `/api/courses/${rosterCourse}/students/resend-setup-all`,
        { method: "POST" },
      );
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        flash(d.message || "Setup links sent.", Boolean(d.failed?.length));
      } else {
        flash(
          typeof d.detail === "string" ? d.detail : "Could not send the links.",
          true,
        );
      }
    } catch (err) {
      flash(err.message, true);
    } finally {
      setResendingAll(false);
    }
  };

  const flash = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setSuccess("");
    } else {
      setSuccess(msg);
      setError("");
    }
    setTimeout(() => {
      setSuccess("");
      setError("");
    }, 4000);
  };

  const saveCourseSemester = async (courseId, semesterId) => {
    try {
      const res = await apiFetch(`/api/courses/${courseId}`, {
        method: "PUT",
        body: JSON.stringify({
          semester_id: semesterId ? Number(semesterId) : null,
        }),
      });
      if (res.ok) {
        flash("Semester updated.");
        setEditCourse(null);
        await loadCourses();
      } else {
        const d = await res.json().catch(() => ({}));
        flash(d.detail || "Could not update semester.", true);
      }
    } catch (err) {
      flash(err.message, true);
    }
  };

  const semesterName = (id) => {
    const s = semesters.find((x) => x.id === id);
    return s ? s.name : null;
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    if (!newSemesterId) {
      flash("Pick a semester for this course.", true);
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch("/api/courses", {
        method: "POST",
        body: JSON.stringify({
          course_code: courseCode.trim().toUpperCase(),
          course_name: courseName.trim(),
          semester_id: Number(newSemesterId),
          default_policy: "FCFS",
          max_sessions_per_day: maxSessions ? parseInt(maxSessions) : null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        flash(`Course ${data.course_code} created.`);
        setCourseCode("");
        setCourseName("");
        setMaxSessions("1");
        await loadCourses();
      } else {
        flash(data.detail || "Failed to create course.", true);
      }
    } catch (err) {
      flash(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSingleStudent = async (e) => {
    e.preventDefault();
    if (!enrollCourse) {
      flash("Select a course first.", true);
      return;
    }
    setLoading(true);
    try {
      const csvContent = `"${singleLast},${singleFirst}",${enrollEmail}`;
      const blob = new Blob([csvContent], { type: "text/csv" });
      const formData = new FormData();
      formData.append("file", blob, "single_student.csv");
      const res = await fetch(
        // sync_roster=false → additive add, so the rest of the class is NOT unenrolled
        `/api/courses/${enrollCourse}/import-students?send_emails=${sendEmails}&sync_roster=false`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("gradiance_token")}`,
          },
          body: formData,
        },
      );
      const data = await res.json();
      if (data.success) {
        const s = data.summary || {};
        const alreadyIn =
          s.already_enrolled > 0 &&
          s.newly_enrolled === 0 &&
          s.accounts_created === 0;
        flash(
          alreadyIn
            ? `${singleFirst} ${singleLast} is already enrolled.`
            : `${singleFirst} ${singleLast} added successfully.`,
        );
        setSingleFirst("");
        setSingleLast("");
        setEnrollEmail("");
        // Refresh the roster if it's showing this course
        if (String(rosterCourse) === String(enrollCourse))
          loadRoster(enrollCourse);
      } else {
        flash(data.detail || "Failed to add student.", true);
      }
    } catch (err) {
      flash(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentImport = async (e) => {
    const file = e.target.files[0];
    if (!file || !importCourse) {
      flash("Select a course first.", true);
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // No semester param — backend reads it from the course record
      const res = await fetch(
        `/api/courses/${importCourse}/import-students?send_emails=${sendEmails}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("gradiance_token")}`,
          },
          body: formData,
        },
      );
      const data = await res.json();
      setImportStudentResult(data);
      if (data.success) await loadCourses();
    } catch (err) {
      setImportStudentResult({ success: false, error: err.message });
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const loadRoster = async (courseId, includeInactive = showInactive) => {
    if (!courseId) {
      setRoster([]);
      return;
    }
    setRosterLoading(true);
    try {
      const res = await apiFetch(
        `/api/courses/${courseId}/students?include_inactive=${includeInactive}`,
      );
      if (res.ok) setRoster(await res.json());
      else setRoster([]);
    } catch {
      setRoster([]);
    } finally {
      setRosterLoading(false);
    }
  };

  const setStudentActive = async (student, isActive) => {
    try {
      const res = await apiFetch(
        `/api/courses/${rosterCourse}/students/${student.id}/active`,
        { method: "PUT", body: JSON.stringify({ is_active: isActive }) },
      );
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(
          d.message ||
            (isActive ? "Student reactivated." : "Student deactivated."),
        );
        setTimeout(() => setSuccess(""), 4000);
        await loadRoster(rosterCourse);
      } else {
        setError(d.detail || "Could not update the student.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirmStudent(null);
    }
  };

  return (
    <div className={styles.panel}>
      {success && <div className={styles.success}>{success}</div>}
      {error && <div className={styles.error}>{error}</div>}

      {/* ── Create Course ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Create Course</h3>
        <form onSubmit={handleCreateCourse} className={styles.form}>
          {semesters.length === 0 && (
            <div
              style={{
                fontSize: 12,
                color: "var(--amber)",
                background: "rgba(251,191,36,0.1)",
                border: "1px solid rgba(251,191,36,0.3)",
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 10,
              }}
            >
              No semesters exist yet. Ask an admin to create one before adding
              courses.
            </div>
          )}
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Course Code</label>
              <input
                placeholder="CS255"
                value={courseCode}
                onChange={(e) => setCourseCode(e.target.value)}
                required
                maxLength={20}
              />
            </div>
            <div className={`${styles.field} ${styles.wide}`}>
              <label>Course Name</label>
              <input
                placeholder="Data Structures"
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                required
                maxLength={255}
              />
            </div>
            <div className={styles.field}>
              <label>Sessions/day</label>
              <input
                type="number"
                min="1"
                max="10"
                placeholder="1"
                value={maxSessions}
                onChange={(e) => setMaxSessions(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Semester</label>
              <select
                value={newSemesterId}
                onChange={(e) => setNewSemesterId(e.target.value)}
                required
                disabled={semesters.length === 0}
              >
                {semesters.length === 0 ? (
                  <option value="">No semesters available</option>
                ) : (
                  semesters.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || semesters.length === 0}
          >
            Create Course
          </button>
        </form>
      </section>

      <div className={styles.divider} />

      {/* ── Add Single Student ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Add Single Student</h3>
        <form onSubmit={handleAddSingleStudent} className={styles.form}>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Course</label>
              <select
                value={enrollCourse}
                onChange={(e) => setEnrollCourse(e.target.value)}
                required
              >
                <option value="">Select course…</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.course_code} — {c.course_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>First Name</label>
              <input
                placeholder="Jacob"
                value={singleFirst}
                onChange={(e) => setSingleFirst(e.target.value)}
                required
              />
            </div>
            <div className={styles.field}>
              <label>Last Name</label>
              <input
                placeholder="Bruner"
                value={singleLast}
                onChange={(e) => setSingleLast(e.target.value)}
                required
              />
            </div>
            <div className={`${styles.field} ${styles.wide}`}>
              <label>School Email</label>
              <input
                type="email"
                placeholder="student@uwec.edu"
                value={enrollEmail}
                onChange={(e) => setEnrollEmail(e.target.value)}
                required
              />
            </div>
          </div>
          <div
            className={styles.field}
            style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
          >
            <input
              type="checkbox"
              id="single-send-email"
              style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
              checked={sendEmails}
              onChange={(e) => setSendEmails(e.target.checked)}
              style={{ width: "auto" }}
            />
            <label
              htmlFor="single-send-email"
              style={{ textTransform: "none", letterSpacing: 0, fontSize: 13 }}
            >
              Send welcome email
            </label>
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            Add Student
          </button>
        </form>
      </section>

      <div className={styles.divider} />

      {/* ── Import Students from CSV ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Import Students from CSV</h3>
        <p style={{ fontSize: 12, color: "var(--txt-dim)", marginBottom: 12 }}>
          Format: "Last,First", email — one student per row. Students not in the
          new CSV will be unenrolled. Semester is read automatically from the
          course.
        </p>
        <div className={styles.row}>
          <div className={styles.field}>
            <label>Course</label>
            <select
              value={importCourse}
              onChange={(e) => setImportCourse(e.target.value)}
            >
              <option value="">Select…</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.course_code} — {c.course_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div
          className={styles.field}
          style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
        >
          <input
            type="checkbox"
            id="send-emails"
            style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
            checked={sendEmails}
            onChange={(e) => setSendEmails(e.target.checked)}
            style={{ width: "auto" }}
          />
          <label
            htmlFor="send-emails"
            style={{ textTransform: "none", letterSpacing: 0, fontSize: 13 }}
          >
            Send welcome emails to new students
          </label>
        </div>
        <input
          type="file"
          accept=".csv"
          onChange={handleStudentImport}
          style={{ display: "none" }}
          id="student-import"
        />
        <label
          htmlFor="student-import"
          style={{
            display: "inline-block",
            background: "var(--blue-mid)",
            color: "var(--txt)",
            padding: "9px 16px",
            borderRadius: "var(--radius-sm)",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 500,
            marginTop: 4,
          }}
        >
          Choose CSV &amp; Import
        </label>
        {importStudentResult && (
          <div
            className={
              importStudentResult.success ? styles.success : styles.error
            }
            style={{ marginTop: 12 }}
          >
            {importStudentResult.success ? (
              <>
                ✓ {importStudentResult.summary.accounts_created} accounts
                created, {importStudentResult.summary.newly_enrolled} enrolled,{" "}
                {importStudentResult.summary.emails_sent} emails sent,{" "}
                {importStudentResult.summary.unenrolled} unenrolled.
                {importStudentResult.warnings?.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11 }}>
                    ⚠ {importStudentResult.warnings.join(" | ")}
                  </div>
                )}
              </>
            ) : (
              `Import failed: ${importStudentResult.error || "Unknown error"}`
            )}
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Course List ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Your Courses</h3>
        {courses.length === 0 ? (
          <p className={styles.empty}>No courses yet. Create one above.</p>
        ) : (
          <div className={styles.courseList}>
            {courses.map((c) => (
              <div key={c.id} className={`${styles.courseCard} card`}>
                <div className={styles.courseCode}>{c.course_code}</div>
                <div className={styles.courseName}>{c.course_name}</div>
                <div className={styles.courseMeta}>
                  {c.default_policy} ·{" "}
                  {c.max_sessions_per_day
                    ? `${c.max_sessions_per_day} session/day`
                    : "unlimited"}
                </div>

                {/* Semester — display with inline edit. Existing courses may be
                    unassigned (semester_id null); this is how they get set. */}
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  {editCourse?.id === c.id ? (
                    <div
                      style={{ display: "flex", gap: 6, alignItems: "center" }}
                    >
                      <select
                        value={editCourse.semester_id ?? ""}
                        onChange={(e) =>
                          setEditCourse({
                            ...editCourse,
                            semester_id: e.target.value,
                          })
                        }
                        style={{ fontSize: 12, padding: "3px 6px" }}
                      >
                        <option value="">Unassigned</option>
                        {semesters.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn-ghost"
                        style={{ fontSize: 11, padding: "3px 8px" }}
                        onClick={() =>
                          saveCourseSemester(c.id, editCourse.semester_id)
                        }
                      >
                        Save
                      </button>
                      <button
                        className="btn-ghost"
                        style={{ fontSize: 11, padding: "3px 8px" }}
                        onClick={() => setEditCourse(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <span
                        style={{
                          color: c.semester_id
                            ? "var(--txt-dim)"
                            : "var(--amber)",
                        }}
                      >
                        {c.semester_id
                          ? semesterName(c.semester_id) || "Semester set"
                          : "No semester"}
                      </span>
                      <button
                        className="btn-ghost"
                        style={{ fontSize: 11, padding: "2px 6px" }}
                        onClick={() =>
                          setEditCourse({
                            id: c.id,
                            semester_id: c.semester_id ?? "",
                          })
                        }
                      >
                        {c.semester_id ? "Change" : "Set semester"}
                      </button>
                    </span>
                  )}
                </div>

                <button
                  className="btn-ghost"
                  onClick={() => handleArchive(c)}
                  title="Archive this course (reversible)"
                  style={{
                    marginTop: 10,
                    fontSize: 12,
                    padding: "5px 10px",
                    alignSelf: "flex-start",
                  }}
                >
                  Archive
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Email Settings (per-instructor SMTP) ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Email Settings</h3>
        <p style={{ fontSize: 12, color: "var(--txt-dim)", marginBottom: 6 }}>
          Send student welcome emails from your own mail server. Leave this
          blank to send from the system account on your behalf — either way, no
          student data (FERPA) is ever emailed.
        </p>
        <p
          style={{ fontSize: 11, color: "var(--txt-faint)", marginBottom: 14 }}
        >
          Your password is encrypted before it's stored. If you'd rather not
          store it here at all, use the system backup sender — and note the
          Interview Manager uses your SMTP directly, with that password staying
          on your own machine.{" "}
          {!smtpEncAvailable && (
            <strong style={{ color: "var(--red, #e5484d)" }}>
              Encryption isn't configured on the server yet, so saving is
              disabled.
            </strong>
          )}
        </p>

        <div
          style={{ fontSize: 11, color: "var(--txt-faint)", marginBottom: 10 }}
        >
          Status:{" "}
          {smtpConfigured ? (
            <strong style={{ color: "var(--green, #30a46c)" }}>
              using your personal SMTP
            </strong>
          ) : (
            <strong>using the system sender</strong>
          )}
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 10 }}
        >
          <div className={styles.field}>
            <label>SMTP host</label>
            <input
              value={smtp.host}
              placeholder="smtp.gmail.com"
              onChange={(e) => setSmtp((s) => ({ ...s, host: e.target.value }))}
            />
          </div>
          <div className={styles.field}>
            <label>Port</label>
            <input
              type="number"
              value={smtp.port}
              onChange={(e) =>
                setSmtp((s) => ({ ...s, port: Number(e.target.value) }))
              }
            />
          </div>
        </div>
        <div className={styles.field}>
          <label>Username</label>
          <input
            value={smtp.username}
            placeholder="you@yourschool.edu"
            onChange={(e) =>
              setSmtp((s) => ({ ...s, username: e.target.value }))
            }
          />
        </div>
        <div className={styles.field}>
          <label>
            Password{" "}
            {smtpConfigured && (
              <span style={{ color: "var(--txt-faint)" }}>
                (leave blank to keep current)
              </span>
            )}
          </label>
          <input
            type="password"
            value={smtpPassword}
            placeholder={smtpConfigured ? "••••••••" : "App password"}
            onChange={(e) => setSmtpPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <div className={styles.field}>
            <label>
              From name{" "}
              <span style={{ color: "var(--txt-faint)" }}>(optional)</span>
            </label>
            <input
              value={smtp.from_name}
              placeholder="Dr. Fine"
              onChange={(e) =>
                setSmtp((s) => ({ ...s, from_name: e.target.value }))
              }
            />
          </div>
          <div className={styles.field}>
            <label>
              From email{" "}
              <span style={{ color: "var(--txt-faint)" }}>(optional)</span>
            </label>
            <input
              value={smtp.from_email}
              placeholder="defaults to username"
              onChange={(e) =>
                setSmtp((s) => ({ ...s, from_email: e.target.value }))
              }
            />
          </div>
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            margin: "8px 0 14px",
          }}
        >
          <input
            type="checkbox"
            style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
            checked={smtp.use_tls}
            onChange={(e) =>
              setSmtp((s) => ({ ...s, use_tls: e.target.checked }))
            }
          />
          Use STARTTLS (recommended)
        </label>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="btn-primary"
            onClick={saveSmtp}
            disabled={smtpBusy || !smtpEncAvailable}
          >
            Save
          </button>
          <button className="btn-ghost" onClick={testSmtp} disabled={smtpBusy}>
            Send Test Email
          </button>
          {smtpConfigured && (
            <button
              className="btn-ghost"
              onClick={clearSmtp}
              disabled={smtpBusy}
              style={{ color: "var(--red, #e5484d)" }}
            >
              Revert to System Sender
            </button>
          )}
        </div>

        {smtpResult && (
          <div
            className={smtpResult.ok ? styles.success : styles.error}
            style={{ marginTop: 12 }}
          >
            {smtpResult.ok ? "✓ " : "⚠ "}
            {smtpResult.text}
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Calendar Auto-Open ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Calendar Auto-Open</h3>
        <p style={{ fontSize: 12, color: "var(--txt-dim)", marginBottom: 6 }}>
          Optional. Point Gradiance at your published Outlook calendar and any
          event whose title contains your marker will open a queue
          automatically.
        </p>
        <p
          style={{ fontSize: 11, color: "var(--txt-faint)", marginBottom: 14 }}
        >
          Sessions only ever <strong>open</strong> automatically — closing is
          always manual, and once you close a session it stays closed even if
          the event is still running. All-day events are ignored.
        </p>

        {/* Sync health — auto-open fails silently, so this stays prominent */}
        {cal.enabled && (
          <div
            style={{
              fontSize: 12,
              marginBottom: 12,
              padding: "8px 12px",
              borderRadius: 6,
              background: calStatus.last_sync_error
                ? "rgba(248,113,113,0.1)"
                : "rgba(48,164,108,0.1)",
              border: `1px solid ${calStatus.last_sync_error ? "rgba(248,113,113,0.35)" : "rgba(48,164,108,0.3)"}`,
              color: calStatus.last_sync_error
                ? "var(--red, #e5484d)"
                : "var(--green, #30a46c)",
            }}
          >
            {calStatus.last_sync_error ? (
              <>
                ⚠ Calendar sync failing ({calStatus.consecutive_failures}×) —
                sessions will not auto-open. {calStatus.last_sync_error}
              </>
            ) : (
              <>✓ Calendar synced {fmtTime(calStatus.last_sync_at)}</>
            )}
          </div>
        )}

        <div className={styles.field}>
          <label>
            Calendar feed URL{" "}
            {calStatus.is_configured && (
              <span style={{ color: "var(--txt-faint)" }}>
                (saved — leave blank to keep)
              </span>
            )}
          </label>
          <input
            value={calUrl}
            placeholder={
              calStatus.is_configured
                ? "•••••• saved ••••••"
                : "https://outlook.office365.com/owa/calendar/.../calendar.ics"
            }
            onChange={(e) => setCalUrl(e.target.value)}
          />
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: 10 }}
        >
          <div className={styles.field}>
            <label>Event title marker</label>
            <input
              value={cal.marker}
              onChange={(e) =>
                setCal((c) => ({ ...c, marker: e.target.value }))
              }
            />
          </div>
          <div className={styles.field}>
            <label>Open early (min)</label>
            <input
              type="number"
              min="0"
              max="120"
              value={cal.early_minutes}
              onChange={(e) =>
                setCal((c) => ({ ...c, early_minutes: Number(e.target.value) }))
              }
            />
          </div>
        </div>

        <div className={styles.field}>
          <label>Policy for auto-opened sessions</label>
          <select
            value={cal.default_policy}
            onChange={(e) =>
              setCal((c) => ({ ...c, default_policy: e.target.value }))
            }
          >
            <option value="FCFS">First-Come, First-Served</option>
            <option value="POINTS" disabled>
              Points-Based (disabled)
            </option>
            <option value="RANDOMIZED">Randomized</option>
            <option value="POOLED_FCFS">Pooled FCFS</option>
          </select>
        </div>

        {cal.default_policy === "POOLED_FCFS" && (
          <div className={styles.field}>
            <label>
              Shuffle offset (min before start)
              <span
                style={{
                  color: "var(--txt-faint)",
                  fontWeight: 400,
                  marginLeft: 6,
                }}
              >
                one-time shuffle fires this many minutes before the event start
              </span>
            </label>
            <input
              type="number"
              min="0"
              max="15"
              step="1"
              value={cal.pool_shuffle_offset_minutes}
              onChange={(e) => {
                const v = e.target.value;
                // Allow the field to be briefly empty while typing; coerce to a
                // clamped integer otherwise. Number('') is 0, which would fight
                // the user mid-edit, so keep '' as-is and normalise on blur.
                setCal((c) => ({
                  ...c,
                  pool_shuffle_offset_minutes:
                    v === ""
                      ? ""
                      : Math.max(0, Math.min(15, parseInt(v, 10) || 0)),
                }));
              }}
              onBlur={(e) => {
                // Normalise an empty or out-of-range field back to a real number
                // when the user leaves it, so we never save '' or a stray value.
                const n = parseInt(e.target.value, 10);
                setCal((c) => ({
                  ...c,
                  pool_shuffle_offset_minutes: Number.isFinite(n)
                    ? Math.max(0, Math.min(15, n))
                    : 1,
                }));
              }}
            />
          </div>
        )}

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            margin: "8px 0 14px",
          }}
        >
          <input
            type="checkbox"
            style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
            checked={cal.enabled}
            onChange={(e) => saveCalendar({ enabled: e.target.checked })}
          />
          Enable auto-open from my calendar
        </label>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="btn-primary"
            onClick={() => saveCalendar()}
            disabled={calBusy}
          >
            Save
          </button>
          <button
            className="btn-ghost"
            onClick={previewCalendar}
            disabled={calBusy || !calStatus.is_configured}
          >
            Preview Next 7 Days
          </button>
          {cal.enabled && (
            <button
              className="btn-ghost"
              onClick={syncCalendarNow}
              disabled={calBusy}
            >
              Sync Now
            </button>
          )}
          {calStatus.is_configured && (
            <button
              className="btn-ghost"
              onClick={disableCalendar}
              disabled={calBusy}
              style={{ color: "var(--red, #e5484d)" }}
            >
              Remove Calendar
            </button>
          )}
        </div>

        {calResult && (
          <div
            className={calResult.ok ? styles.success : styles.error}
            style={{ marginTop: 12 }}
          >
            {calResult.ok ? "✓ " : "⚠ "}
            {calResult.text}
          </div>
        )}

        {calPreview && (
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--txt-faint)",
                marginBottom: 8,
              }}
            >
              {calPreview.count} matching event
              {calPreview.count !== 1 ? "s" : ""} for &nbsp;“{calPreview.marker}
              ” in the next 7 days
            </div>
            {calPreview.count === 0 ? (
              <p style={{ fontSize: 13, color: "var(--txt-faint)" }}>
                Nothing matched. Check that your event titles contain the marker
                exactly.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {calPreview.occurrences.map((o, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 12,
                      padding: "8px 10px",
                      borderRadius: 6,
                      background: "var(--bg-card)",
                      border: "1px solid var(--border)",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                  >
                    <span style={{ color: "var(--txt)" }}>{o.summary}</span>
                    <span
                      style={{
                        color: "var(--txt-faint)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      opens {fmtTime(o.opens_at)}
                      {o.already_handled && " · already handled"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Course Competencies (view + per-course import) ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Course Competencies</h3>
        <p style={{ fontSize: 12, color: "var(--txt-dim)", marginBottom: 12 }}>
          Pick a course to view its competencies, or upload an Interview Manager
          export for just that course (any filename). The uploaded list is the
          source of truth: competencies in it are added or kept, any not in it
          are removed, and if the file's course code differs, this course is
          renamed to match it.
        </p>
        <div className={styles.field}>
          <label>Course</label>
          <select
            value={compCourse}
            onChange={(e) => {
              setCompCourse(e.target.value);
              setCompResult(null);
              loadCompetencies(e.target.value);
            }}
          >
            <option value="">Select course…</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.course_code} — {c.course_name}
              </option>
            ))}
          </select>
        </div>

        {compCourse && (
          <>
            <input
              type="file"
              accept=".json"
              onChange={handleCourseCompImport}
              style={{ display: "none" }}
              id="course-comp-import"
            />
            <label
              htmlFor="course-comp-import"
              style={{
                display: "inline-block",
                background: "var(--blue-mid)",
                color: "var(--txt)",
                padding: "9px 16px",
                borderRadius: "var(--radius-sm)",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 500,
                marginTop: 4,
              }}
            >
              Upload Competency List for This Course
            </label>
          </>
        )}

        {compResult && (
          <div
            className={compResult.ok ? styles.success : styles.error}
            style={{ marginTop: 12 }}
          >
            {compResult.ok ? "✓ " : "⚠ "}
            {compResult.text}
            {compResult.warnings?.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 11 }}>
                {compResult.warnings.join(" ")}
              </div>
            )}
          </div>
        )}

        {compLoading && (
          <p style={{ fontSize: 13, color: "var(--txt-dim)", marginTop: 10 }}>
            Loading…
          </p>
        )}

        {!compLoading && competencies.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--txt-faint)",
                marginBottom: 8,
              }}
            >
              {competencies.length} competenc
              {competencies.length !== 1 ? "ies" : "y"}
            </div>
            <ol
              style={{
                margin: 0,
                paddingLeft: 20,
                fontSize: 13,
                color: "var(--txt)",
              }}
            >
              {competencies.map((c) => (
                <li key={c.course_competency_id} style={{ padding: "3px 0" }}>
                  {c.name}
                </li>
              ))}
            </ol>
          </div>
        )}

        {compCourse && !compLoading && competencies.length === 0 && (
          <p style={{ fontSize: 13, color: "var(--txt-faint)", marginTop: 8 }}>
            No competencies for this course yet. Upload a list to add them.
          </p>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Archived Courses ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Archived Courses</h3>
        {archived.length === 0 ? (
          <p className={styles.empty}>No archived courses.</p>
        ) : (
          <>
            <p
              style={{
                fontSize: 12,
                color: "var(--txt-dim)",
                marginBottom: 12,
              }}
            >
              Archived courses are hidden from active lists and the queue.
              Restore one to bring it back, or permanently delete to scrub it
              for good.
            </p>
            <div className={styles.courseList}>
              {archived.map((c) => (
                <div
                  key={c.id}
                  className={`${styles.courseCard} card`}
                  style={{ opacity: 0.85 }}
                >
                  <div className={styles.courseCode}>{c.course_code}</div>
                  <div className={styles.courseName}>{c.course_name}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button
                      className="btn-ghost"
                      onClick={() => handleRestore(c)}
                      title="Restore this course"
                      style={{ fontSize: 12, padding: "5px 10px" }}
                    >
                      Restore
                    </button>
                    <button
                      onClick={() => handleDeletePermanently(c)}
                      title="Permanently delete this course and all its data"
                      style={{
                        fontSize: 12,
                        padding: "5px 10px",
                        background: "transparent",
                        color: "var(--red, #e5484d)",
                        border: "1px solid var(--red, #e5484d)",
                        borderRadius: "var(--radius-sm, 6px)",
                        cursor: "pointer",
                      }}
                    >
                      Delete permanently
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Student Roster ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Student Roster</h3>
        <div className={styles.field}>
          <label>Course</label>
          <select
            value={rosterCourse}
            onChange={(e) => {
              setRosterCourse(e.target.value);
              loadRoster(e.target.value);
            }}
          >
            <option value="">Select course…</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.course_code} — {c.course_name}
              </option>
            ))}
          </select>
        </div>

        {rosterLoading && (
          <p style={{ fontSize: 13, color: "var(--txt-dim)" }}>Loading…</p>
        )}

        {roster.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 11, color: "var(--txt-faint)" }}>
                {roster.filter((s) => s.is_active !== false).length} active
                {roster.some((s) => s.is_active === false) &&
                  `, ${roster.filter((s) => s.is_active === false).length} deactivated`}
              </div>
              {/* Only when someone actually needs a link — the count on the
                  button is the check that it's about to email who you expect. */}
              {(() => {
                const pending = roster.filter(
                  (s) => s.is_active !== false && !s.password_set,
                ).length;
                if (pending === 0) return null;
                return (
                  <button
                    className="btn-ghost"
                    onClick={resendSetupAll}
                    disabled={resendingAll}
                    title="Email a new setup link to every student who hasn't set a password"
                    style={{ fontSize: 12, padding: "4px 10px" }}
                  >
                    {resendingAll
                      ? "Sending…"
                      : `Notify ${pending} not set up`}
                  </button>
                );
              })()}
              <label
                style={{
                  fontSize: 12,
                  color: "var(--txt-dim)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    loadRoster(rosterCourse, e.target.checked);
                  }}
                  style={{ width: 15, height: 15 }}
                />
                Show deactivated
              </label>
            </div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "6px 8px",
                      color: "var(--txt-dim)",
                      fontWeight: 600,
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "6px 8px",
                      color: "var(--txt-dim)",
                      fontWeight: 600,
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "6px 8px",
                      color: "var(--txt-dim)",
                      fontWeight: 600,
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "6px 8px",
                      color: "var(--txt-dim)",
                      fontWeight: 600,
                      borderBottom: "1px solid var(--border)",
                    }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {roster.map((s) => {
                  const deactivated = s.is_active === false;
                  return (
                    <tr
                      key={s.id}
                      style={{
                        borderBottom: "1px solid var(--border)",
                        opacity: deactivated ? 0.55 : 1,
                      }}
                    >
                      <td style={{ padding: "7px 8px", color: "var(--txt)" }}>
                        {s.display_name}
                      </td>
                      <td
                        style={{ padding: "7px 8px", color: "var(--txt-dim)" }}
                      >
                        {s.email}
                      </td>
                      <td style={{ padding: "7px 8px" }}>
                        {deactivated ? (
                          <span
                            style={{ color: "var(--txt-faint)", fontSize: 11 }}
                          >
                            ⊘ Deactivated
                          </span>
                        ) : s.password_set ? (
                          <span style={{ color: "var(--green)", fontSize: 11 }}>
                            ✓ Active
                          </span>
                        ) : (
                          <span style={{ color: "var(--amber)", fontSize: 11 }}>
                            ⏳ Pending setup
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "7px 8px", textAlign: "right" }}>
                        {deactivated ? (
                          <button
                            className="btn-ghost"
                            onClick={() =>
                              setConfirmStudent({
                                student: s,
                                deactivate: false,
                              })
                            }
                            style={{
                              fontSize: 11,
                              padding: "3px 8px",
                              color: "var(--green)",
                            }}
                          >
                            Reactivate
                          </button>
                        ) : (
                          <>
                            {/* Only shown while a student is still pending —
                                once they have a password the reset flow is the
                                right tool, and the endpoint refuses anyway. */}
                            {!s.password_set && (
                              <button
                                className="btn-ghost"
                                onClick={() => resendSetup(s)}
                                disabled={resendingId === s.id}
                                title={`Send ${s.display_name} a new setup link`}
                                style={{
                                  fontSize: 11,
                                  padding: "3px 8px",
                                  marginRight: 6,
                                }}
                              >
                                {resendingId === s.id
                                  ? "Sending…"
                                  : "Resend link"}
                              </button>
                            )}
                            <button
                              className="btn-ghost"
                              onClick={() =>
                                setConfirmStudent({
                                  student: s,
                                  deactivate: true,
                                })
                              }
                              style={{
                                fontSize: 11,
                                padding: "3px 8px",
                                color: "var(--red, #e5484d)",
                              }}
                            >
                              Deactivate
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {rosterCourse && !rosterLoading && roster.length === 0 && (
          <p style={{ fontSize: 13, color: "var(--txt-faint)", marginTop: 8 }}>
            No students enrolled yet.
          </p>
        )}
      </section>

      {confirmStudent && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setConfirmStudent(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              padding: 24,
              width: 400,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ fontWeight: 600, color: "var(--txt)", fontSize: 15 }}>
              {confirmStudent.deactivate ? "Deactivate" : "Reactivate"}{" "}
              {confirmStudent.student.display_name}?
            </div>
            <div
              style={{ fontSize: 13, color: "var(--txt-dim)", lineHeight: 1.5 }}
            >
              {confirmStudent.deactivate ? (
                <>
                  They'll be removed from the roster and won't be able to join
                  future queue sessions. All their queue history and analytics
                  stay in the course. A session that's live right now isn't
                  affected — they lose access starting with the next session.
                </>
              ) : (
                <>
                  They'll return to the active roster and can join queue
                  sessions again.
                </>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 4,
              }}
            >
              <button
                className="btn-ghost"
                onClick={() => setConfirmStudent(null)}
              >
                Cancel
              </button>
              <button
                className={
                  confirmStudent.deactivate ? "btn-danger" : "btn-primary"
                }
                onClick={() =>
                  setStudentActive(
                    confirmStudent.student,
                    !confirmStudent.deactivate,
                  )
                }
              >
                {confirmStudent.deactivate ? "Deactivate" : "Reactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.divider} />

      {/* Same component students use — instructors have display names and
          pronouns too, and one implementation means one set of rules. */}
      <section className={styles.section}>
        <ProfileCard />
      </section>

      <div className={styles.divider} />

      {/* Instructors export their own courses here; the picker and delivery
          options are scoped server-side, so this is the same component the
          Admin tab uses with a narrower set of choices. */}
      <ExportCard courses={courses} />

      <QueueMessagesSection />
      <div className={styles.divider} />
      <PolicyImageSection />

      <TestStudentsSection />
    </div>
  );
}
